import { FileDownload } from "@blocks/general/file-download-group"
import { ARROW_DOWN, ARROW_UP, BEV, HFCEV, INFO, PHEV } from "@components/atoms/Icons"
import {
  EVTableContainer,
  FilterBlock,
  FloatingFilter,
  FuelTypeFilter,
  TT,
  TableWrapper,
  vehicleTypes,
} from "@components/atoms/data/Components"
import { Grid, Col } from "@components/layout/Grid"
import { VSpace } from "@components/layout/Spacer"
import { useEvVehicleSpecs } from "@hooks/queries"
import { cn, tv } from "@utils/tw"
import { numberWithCommas } from "@utils/numberWithCommas"
import { EditableText, defineBlock } from "eddev/blocks"
import { useHydrating } from "eddev/routing"
import { ComponentPropsWithoutRef, forwardRef, useMemo, useRef, useState } from "react"
import { AnimatePresence, useInView } from "framer-motion"
import { useIsMobile } from "@hooks/useViewportDerived"
import { Button } from "@components/atoms/Button"
import { CarTypeFilter } from "@components/atoms/data/CarTypeFilter"

export const meta: BlockMeta = {
  title: "EV Index Vehicle specification information",
  category: "common",
  icon: "editor-table",
  keywords: ["ev index"],
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post", "data-embed"],
    multiple: false,
  },
}

type SortConfig = {
  key: string // Allows sorting by "All fuels" as well
  direction: "ascending" | "descending"
}

export default defineBlock("data/evindex-vehicle-specification-information", (props) => {
  const hydrating = useHydrating()
  const data = useEvVehicleSpecs()
  const [currentFuelType, setCurrentFuelType] = useState<"BEV" | "PHEV" | "HFCEV">("BEV")
  const [currentVehicleType, setCurrentVehicleType] = useState<(keyof typeof vehicleTypes)[]>(["all"])

  const parsedData = useMemo(() => {
    if (data.data?.evVehicleSpecs) {
      return data.data.evVehicleSpecs.map((data) => JSON.parse(data?.data ?? ""))
    }
    return []
  }, [data.data?.evVehicleSpecs])

  // State for sorting
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "MODEL",
    direction: "ascending",
  })

  const sortedData = useMemo(() => {
    if (!parsedData) return null
    const sorted = [...parsedData].sort((a, b) => {
      let compareA, compareB
      compareA = a[sortConfig.key]
      compareB = b[sortConfig.key]

      if (sortConfig.key === "VARIANT DETAILS") {
        compareA = a["MODEL"]
        compareB = b["MODEL"]
      }

      if (sortConfig.key === "FAST CHARGE TIME (minutes)") {
        compareA = a[sortConfig.key] ? parseInt(a[sortConfig.key].split()[0]) : 99999
        compareB = b[sortConfig.key] ? parseInt(b[sortConfig.key].split()[0]) : 99999
      }

      if (sortConfig.key === "RANGE (km)") {
        compareA = a[sortConfig.key] ? parseInt(a[sortConfig.key]) : 0
        compareB = b[sortConfig.key] ? parseInt(b[sortConfig.key]) : 0
      }

      if (sortConfig.key === "ENERGY CONSUMPTION") {
        compareA = a[sortConfig.key] ? parseFloat(a[sortConfig.key]) : 9999
        compareB = b[sortConfig.key] ? parseFloat(b[sortConfig.key]) : 9999
      }

      if (sortConfig.key === "ANCAP RATING") {
        const sortOrder: (string | undefined)[] = [
          "5 star",
          "4 star",
          "3 star",
          "2 star",
          "1 star",
          "Unrated",
          undefined,
        ]

        const extractInfo = (str: string | undefined): [string | undefined, number] => {
          if (typeof str === "undefined") return [undefined, 0]
          const parts = str.split(", ")
          const rating: string = parts[0]
          const year: number = parts[1] ? parseInt(parts[1], 10) : 0
          return [rating, year]
        }

        const [ratingA, yearA] = extractInfo(a[sortConfig.key])
        const [ratingB, yearB] = extractInfo(b[sortConfig.key])

        // Compare by star rating first
        const ratingOrderA = sortOrder.indexOf(ratingA)
        const ratingOrderB = sortOrder.indexOf(ratingB)

        if (ratingOrderA !== ratingOrderB) {
          return sortConfig.direction === "ascending" ? ratingOrderA - ratingOrderB : ratingOrderB - ratingOrderA
        }

        // If star ratings are the same, compare by year
        if (yearA < yearB) {
          return sortConfig.direction === "ascending" ? 1 : -1
        }
        if (yearA > yearB) {
          return sortConfig.direction === "ascending" ? -1 : 1
        }

        return 0
      }

      if (sortConfig.key === "LISTED PRICE ($AUD)") {
        compareA = a[sortConfig.key] ? parseInt(a[sortConfig.key]) : 99999999
        compareB = b[sortConfig.key] ? parseInt(b[sortConfig.key]) : 99999999
      }

      if (compareA < compareB) {
        return sortConfig.direction === "ascending" ? -1 : 1
      }
      if (compareA > compareB) {
        return sortConfig.direction === "ascending" ? 1 : -1
      }
      return 0
    })

    if (!currentVehicleType.includes("all")) {
      const filtered: any[] = []

      currentVehicleType.forEach((type) => {
        sorted
          .filter((item) => item["VEHICLE TYPE"] === vehicleTypes[type])
          .forEach((result) => {
            filtered.push(result)
          })
      })

      return filtered.filter((data) => data["FUEL TYPE"] === currentFuelType)
    }

    return sorted.filter((data) => data["FUEL TYPE"] === currentFuelType)
  }, [parsedData, sortConfig, currentVehicleType, currentFuelType])

  // Handler for sorting
  const requestSort = (key: string) => {
    let direction: "ascending" | "descending" = "ascending"
    if (key !== "VEHICLE TYPE") {
      direction = "descending" // Set default to descending for all other columns
    }
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "ascending" ? "descending" : "ascending" // Toggle direction if the same column is clicked again
    }
    setSortConfig({ key, direction })
  }

  const isMobile = useIsMobile()
  const [filterOpen, setFilterOpen] = useState<boolean>(false)
  const blockRef = useRef<HTMLDivElement>(null)
  const inView = useInView(blockRef, { amount: 0.5 })

  return (
    <EVTableContainer ref={blockRef}>
      <div className="grid-auto px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px]">
        <Col initial={12} laptop={6}>
          <EditableText as="p" placeholder="Enter content..." store="content" />
          <VSpace initial={6} />
        </Col>

        <Col initial={12} laptop={6} className="flex flex-col justify-end">
          {props.downloadData?.mediaItemUrl && props.downloadData?.fileSize && (
            <FileDownload
              title="Download this data"
              file={props.downloadData.mediaItemUrl}
              size={props.downloadData.fileSize}
              underline
            />
          )}
          <VSpace initial={6} />
        </Col>

        {isMobile && (
          <AnimatePresence>
            {inView && (
              <FloatingFilter open={filterOpen} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Button
                  size="tiny"
                  color="yellow"
                  icon={filterOpen ? "CARET_DOWN" : "CARET_UP"}
                  iconStyle="right"
                  onClick={() => setFilterOpen(!filterOpen)}
                >
                  Filter this data
                </Button>

                <FuelTypeFilter className="[&_ul]:justify-center">
                  <ul>
                    <li>
                      <button
                        className={`${currentFuelType === "BEV" ? `active` : ``} bev`}
                        onClick={() => setCurrentFuelType("BEV")}
                      >
                        <i>{BEV}</i>
                        <span>BEV</span>
                      </button>
                    </li>
                    <li>
                      <button
                        className={`${currentFuelType === "PHEV" ? `active` : ``} phev`}
                        onClick={() => setCurrentFuelType("PHEV")}
                      >
                        <i>{PHEV}</i>
                        <span>PHEV</span>
                      </button>
                    </li>
                    <li>
                      <button
                        className={`${currentFuelType === "HFCEV" ? `active` : ``} hfcev`}
                        onClick={() => setCurrentFuelType("HFCEV")}
                      >
                        <i>{HFCEV}</i>
                        <span>HFCEV</span>
                      </button>
                    </li>
                  </ul>
                </FuelTypeFilter>

                <CarTypeFilter currentVehicleType={currentVehicleType} setCurrentVehicleType={setCurrentVehicleType} />

                <VSpace initial={6} />
              </FloatingFilter>
            )}
          </AnimatePresence>
        )}
        {!isMobile && (
          <Col initial={12}>
            <FilterBlock>
              <FuelTypeFilter>
                <ul>
                  <li>
                    <button
                      className={`${currentFuelType === "BEV" ? `active` : ``} bev`}
                      onClick={() => setCurrentFuelType("BEV")}
                    >
                      <i>{BEV}</i>
                      <span>BEV</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={`${currentFuelType === "PHEV" ? `active` : ``} phev`}
                      onClick={() => setCurrentFuelType("PHEV")}
                    >
                      <i>{PHEV}</i>
                      <span>PHEV</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={`${currentFuelType === "HFCEV" ? `active` : ``} hfcev`}
                      onClick={() => setCurrentFuelType("HFCEV")}
                    >
                      <i>{HFCEV}</i>
                      <span>HFCEV</span>
                    </button>
                  </li>
                </ul>
              </FuelTypeFilter>

              <CarTypeFilter currentVehicleType={currentVehicleType} setCurrentVehicleType={setCurrentVehicleType} />
            </FilterBlock>
          </Col>
        )}

        {!hydrating && sortedData && (
          <Col initial={12}>
            <TableWrapper>
              <TableContainer>
                <table>
                  <thead>
                    <tr>
                      <th onClick={() => requestSort("MODEL")} className="sortable">
                        <TT>
                          Model
                          {sortConfig.key === "MODEL" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("VEHICLE TYPE")} className="sortable">
                        <TT>
                          Vehicle Type
                          {sortConfig.key === "VEHICLE TYPE" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("VARIANT DETAILS")} className="sortable">
                        <TT>
                          Variant Details
                          {sortConfig.key === "VARIANT DETAILS" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("FAST CHARGE TIME (minutes)")} className="sortable">
                        <TT>
                          Fast Charge Time
                          <br />
                          (Minutes)
                          {sortConfig.key === "FAST CHARGE TIME (minutes)" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("RANGE (km)")} className="sortable">
                        <TT>
                          Range (km)
                          {sortConfig.key === "RANGE (km)" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("ENERGY CONSUMPTION")} className="sortable">
                        <TT>
                          Energy Consumption
                          <br />
                          (kWh/100km)
                          {sortConfig.key === "ENERGY CONSUMPTION" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("ANCAP RATING")} className="sortable">
                        <TT>
                          ANCAP Safety Rating
                          {sortConfig.key === "ANCAP RATING" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                      <th onClick={() => requestSort("LISTED PRICE ($AUD)")} className="sortable">
                        <TT>
                          Listed Price ($AUD)
                          {sortConfig.key === "LISTED PRICE ($AUD)" ? (
                            <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                          ) : (
                            <i>{ARROW_DOWN}</i>
                          )}
                        </TT>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {sortedData.map((data, i) => (
                      <tr key={i}>
                        <td>{data["MODEL"]}</td>
                        <td>{data["VEHICLE TYPE"]}</td>
                        <td>{data["VARIANT DETAILS"]}</td>
                        <td>{data["FAST CHARGE TIME (minutes)"]}</td>
                        <td>{data["RANGE (km)"]}</td>
                        <td>{data["ENERGY CONSUMPTION"]}</td>
                        <td>
                          {data["ANCAP VAN RATING"] ? data["ANCAP VAN RATING"] : formatAncap(data["ANCAP RATING"])}
                        </td>
                        <td>{numberWithCommas(data["LISTED PRICE ($AUD)"] ?? "")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableContainer>
            </TableWrapper>
          </Col>
        )}
        <Col initial={12} laptop={6}>
          <VSpace initial={6} />
          <EditableText as="p" placeholder="Enter footnote..." store="footnote" />
        </Col>
      </div>
    </EVTableContainer>
  )
})

const tableContainerStyles = tv({
  base: "max-h-[70vh] overflow-scroll mt-6 [&_table]:mt-0 [&_thead]:sticky [&_thead]:top-0",
})

const TableContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(tableContainerStyles(), className)} {...props} />
))
TableContainer.displayName = "TableContainer"

const formatAncap = (rating: string) => {
  if (rating === "Unrated" || rating === "" || !rating) return "Unrated"
  const parts = rating?.split(", ") ?? []

  let stars = ""
  switch (parts[0]) {
    case "5 star":
      stars = "★★★★★"
      break

    case "4 star":
      stars = "★★★★"
      break

    case "3 star":
      stars = "★★★"
      break

    case "2 star":
      stars = "★★"
      break

    case "1 star":
      stars = "★"
      break
    default:
      break
  }

  return (
    <>
      {stars} ({parts[1]})
    </>
  )
}
